'use client'

import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import type { NotificationView } from '@package/notification'
import { Button, Skeleton } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

import type { NotificationTarget } from '../../context/notification-resolver'
import type { NotificationFilter } from './filter-tabs'
import { useSharedTRPC } from '../../context'
import { useNotificationResolver } from '../../context/notification-resolver'
import { ShareRecipientsDialog } from '../../lib/share-recipients-dialog'
import { ActorNotificationCard } from './actor-card'
import { NotificationFilterTabs } from './filter-tabs'
import { RecipientNotificationCard } from './recipient-card'

interface NotificationListProps {
  /** Injected by the app so this package stays router-agnostic. */
  onNavigate: (target: NotificationTarget) => void
  availableFilters: readonly NotificationFilter[]
  /**
   * Controlled tab. Both are optional so the component still works with no router at
   * all — Storybook renders it without one, and this package must not depend on one.
   * Pass both to drive the tab from a search param.
   */
  filter?: NotificationFilter
  onFilterChange?: (next: NotificationFilter) => void
  /** Empty-state copy. Defaults suit a recipient; senders and children override the description. */
  emptyTitle?: string
  emptyDescription?: string
  className?: string
}

export function NotificationList({
  onNavigate,
  availableFilters,
  filter: filterProp,
  onFilterChange,
  emptyTitle = 'Yah, Kosong...',
  emptyDescription = 'Belum ada pesan untukmu.',
  className,
}: NotificationListProps) {
  const [uncontrolledFilter, setUncontrolledFilter] = useState<NotificationFilter>('ALL')
  const filter = filterProp ?? uncontrolledFilter

  function handleFilterChange(next: NotificationFilter) {
    // Kept in sync even when controlled, so a caller that later drops the prop does
    // not snap the tab back to ALL.
    setUncontrolledFilter(next)
    onFilterChange?.(next)
  }
  // The sender's own card opens a recipient read-status dialog instead of navigating.
  const [actorNotification, setActorNotification] = useState<NotificationView | null>(null)
  const resolver = useNotificationResolver()
  const queryClient = useQueryClient()
  const trpc = useSharedTRPC()

  // `ALL` and `UNREAD` are not categories — they span every kind, so they send `null` and let the
  // `unreadOnly` flag do the narrowing. Every other tab is named after the category it filters on.
  const category = filter === 'ALL' || filter === 'UNREAD' ? null : filter

  const { data, isPending, isError, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useInfiniteQuery({
    ...trpc.notification.list.infiniteQueryOptions(
      { pageSize: 20, category, unreadOnly: filter === 'UNREAD' },
      { getNextPageParam: (lastPage) => lastPage.nextCursor },
    ),
    /**
     * An ACTOR row's "N dari M sudah membuka" changes because of someone *else's* action —
     * a recipient opening the share bumps `NotificationEvent.readCount` server-side, with
     * nothing on the sender's client to invalidate off. Without a poll the line only moves
     * on remount or a hard reload.
     *
     * `refetchOnWindowFocus` is not enough on its own: query-core subscribes to
     * `visibilitychange` only, so a parent watching this feed in one window while a child
     * reads in a second window never leaves `visibilityState: 'visible'` and never refetches.
     *
     * 30s is a compromise — a refetch of an infinite query re-runs every loaded page, and a
     * deep-scrolled feed pays one request per page. `refetchIntervalInBackground` stays at
     * its default `false`, so a hidden tab does not poll at all.
     */
    refetchInterval: 30_000,
  })

  const markRead = useMutation(
    // The invalidation lives on `mutationOptions`, not on the `mutate(...)` call: the same
    // click that marks a row read also navigates away, and callbacks passed to `mutate` are
    // dropped when the calling component unmounts before the request settles. Mutation-level
    // callbacks run off the mutation cache, so they still fire after the route has changed.
    trpc.notification.markRead.mutationOptions({
      onSuccess: () =>
        Promise.all([
          // The badge lives in the app layout, so it stays mounted and refetches at once.
          queryClient.invalidateQueries({ queryKey: trpc.notification.unreadCount.queryKey() }),
          // No input: the feed key is per-filter, and one read touches every tab — the row
          // drops out of "Belum dibaca" and loses its dot in "Semua". Whichever tab is
          // mounted refetches now; the rest are marked stale and refetch on next mount.
          queryClient.invalidateQueries({ queryKey: trpc.notification.list.infiniteQueryKey() }),
        ]),
    }),
  )
  // Destructuring `mutate` gives a reference that TanStack Query keeps stable for the
  // lifetime of the component (it is memoized off the mutation observer, not recreated
  // per render), so it can sit in the dep array below without re-running the effect and
  // without needing an exhaustive-deps disable.
  const { mutate: markSeen } = useMutation(trpc.notification.markSeen.mutationOptions())

  // Clears the ACTOR-side "new activity" state once, on feed open — not per card.
  useEffect(() => {
    markSeen()
  }, [markSeen])

  // Infinite scroll: the next page loads when the sentinel nears the viewport, so the
  // feed never asks for a click to keep reading. `rootMargin` fires the fetch while the
  // sentinel is still below the fold, so the rows are usually in place by the time the
  // user scrolls to them.
  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const element = sentinelRef.current
    if (!element) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) void fetchNextPage()
      },
      { rootMargin: '400px' },
    )
    observer.observe(element)
    return () => observer.disconnect()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  /**
   * De-duplicated by id across pages.
   *
   * A grouped card moves back to the head of the feed when its count changes
   * (`emitRollup` rewrites `createdAt` so a resurfaced card is not left three screens
   * down). If that happens between two page fetches, the keyset cursor can hand back a
   * row an earlier page already carried — React would then see two children with the
   * same key and drop one at random. First occurrence wins; it is the newer page.
   */
  const seenIds = new Set<string>()
  const rows: NotificationView[] = (data?.pages.flatMap((page) => page.data) ?? []).filter((row) => {
    if (seenIds.has(row.id)) return false
    seenIds.add(row.id)
    return true
  })

  /**
   * The actor dialog lists who received a share and who has opened it, so only a share has one to
   * show. Any other actor row is a receipt — nothing to open — and must render inert rather than
   * as a button that does nothing.
   */
  function actorDialogAvailable(notification: NotificationView) {
    return notification.audience === 'ACTOR' && notification.category === 'SHARE'
  }

  function handleSelect(notification: NotificationView) {
    // An ACTOR row has no destination of its own — the sender is looking at their own
    // send, so the dialog *is* the destination. Returning here also keeps the resolver
    // out of it: the sender is not navigating to the shared content.
    if (notification.audience === 'ACTOR') {
      if (actorDialogAvailable(notification)) setActorNotification(notification)
      return
    }

    // Only on click — never on render or tab open — otherwise the "Belum dibaca" tab
    // would mark everything read as soon as it renders and erase its own contents.
    if (notification.isUnread) {
      markRead.mutate({ notificationId: notification.id })
    }
    onNavigate(resolver(notification))
  }

  return (
    <section className={cn('srd:flex srd:w-full srd:flex-col srd:gap-y-6', className)}>
      <NotificationFilterTabs value={filter} onChange={handleFilterChange} available={availableFilters} />

      {isError && (
        <div className='srd:flex srd:flex-col srd:items-center srd:gap-y-3 srd:py-16 srd:text-center'>
          <p className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>
            Oops, Terjadi Kesalahan
          </p>
          <Button
            onClick={() => refetch()}
            className='srd:bg-blue-20 srd:text-blue-100 srd:rounded-full srd:px-6 srd:py-2'
          >
            Coba Lagi
          </Button>
        </div>
      )}

      {isPending && (
        <div className='srd:flex srd:flex-col srd:gap-y-3'>
          {new Array(4).fill(0).map((_, index) => (
            <Skeleton key={index} className='srd:h-24 srd:w-full srd:rounded-xl' />
          ))}
        </div>
      )}

      {!isPending && !isError && rows.length === 0 && (
        <div className='srd:flex srd:flex-col srd:items-center srd:gap-y-3 srd:py-16 srd:text-center'>
          <p className={cn(typographyVariants({ variant: 'title-md' }), 'srd:text-neutral-white')}>{emptyTitle}</p>
          <p className={cn(typographyVariants({ variant: 'body-sm' }), 'srd:text-neutral-white')}>{emptyDescription}</p>
        </div>
      )}

      <div className='srd:flex srd:flex-col srd:gap-y-3'>
        {rows.map((notification) =>
          notification.audience === 'ACTOR' ? (
            <ActorNotificationCard
              key={notification.id}
              notification={notification}
              // Omitted, not a no-op handler: `NotificationCard` reads the absence of `onSelect` as
              // "inert" and drops the pointer cursor, the button role and the tab stop with it.
              onSelect={actorDialogAvailable(notification) ? () => handleSelect(notification) : undefined}
            />
          ) : (
            <RecipientNotificationCard
              key={notification.id}
              notification={notification}
              onSelect={() => handleSelect(notification)}
            />
          ),
        )}
      </div>

      {/*
        Rendered on `hasNextPage`, never on `rows.length`: the server filters `unreadOnly`
        in JS after mapping, so a page can legitimately come back with fewer than
        `pageSize` rows while more still exist upstream (see task-10-report.md).

        The sentinel keeps its padding when idle so it has real height for the observer
        to intersect, and shows skeletons in place of the rows being fetched.
      */}
      {hasNextPage && (
        <div ref={sentinelRef} className='srd:flex srd:flex-col srd:gap-y-3 srd:py-2'>
          {isFetchingNextPage &&
            new Array(2)
              .fill(0)
              .map((_, index) => <Skeleton key={index} className='srd:h-24 srd:w-full srd:rounded-xl' />)}
        </div>
      )}

      {/*
        Mounted only while a card is selected, so closing discards the query and a
        re-open refetches. Recipients read the share between openings, and a stale
        tick is the one thing this dialog must not show.
      */}
      {actorNotification && (
        <ShareRecipientsDialog
          open
          onOpenChange={(next) => {
            if (!next) setActorNotification(null)
          }}
          notification={actorNotification}
        />
      )}
    </section>
  )
}
